import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiClients, API_V_LATEST } from "@/axios-config";
import {
    UserNotificationCreateRequestDto,
    NotificationMarkAsReadRequestDto,
    NotificationMarkAsDismissedRequestDto,
} from "@/api";
import { handleErrorToast } from "@/lib/utils";
import { toast } from "sonner";
import i18next from "i18next";
import { Roles } from "@/auth/user-roles";
import {
    useRoleGatedQuery,
    useRoleGatedMutation,
} from "@/hooks/use-role-gated-hooks";
import { invalidateQueryKeys } from "@/hooks/query-invalidation";
import {
    STALE_TIME_30_SECONDS,
    REFETCH_INTERVAL_1_MINUTE,
} from "@/hooks/query-config";

export const UserNotificationQueryKeys = {
    notifications: "user-notifications",
    summary: "user-notifications-summary",
    undismissed: "user-notifications-undismissed",
    pending: "user-notifications-pending",
};

export const useUserNotifications = (
    isRead?: boolean,
    isDismissed?: boolean,
    limit: number = 50,
    offset: number = 0,
) => {
    return useQuery({
        queryKey: [
            UserNotificationQueryKeys.notifications,
            isRead,
            isDismissed,
            limit,
            offset,
        ],
        queryFn: async () => {
            try {
                const res = await ApiClients.Notification.apiNotificationGet(
                    isRead,
                    isDismissed,
                    limit,
                    offset,
                    API_V_LATEST,
                );
                return res.data;
            } catch (err) {
                void handleErrorToast(err);
                return [];
            }
        },
        staleTime: STALE_TIME_30_SECONDS,
    });
};

export const useUserNotificationSummary = () => {
    return useQuery({
        queryKey: [UserNotificationQueryKeys.summary],
        queryFn: async () => {
            try {
                const res =
                    await ApiClients.Notification.apiNotificationSummaryGet(
                        API_V_LATEST,
                    );
                return res.data;
            } catch (err) {
                void handleErrorToast(err);
                return { totalCount: 0, unreadCount: 0, undismissedCount: 0 };
            }
        },
        staleTime: STALE_TIME_30_SECONDS,
        refetchInterval: REFETCH_INTERVAL_1_MINUTE,
    });
};

export const useUndismissedNotifications = () => {
    return useQuery({
        queryKey: [UserNotificationQueryKeys.undismissed],
        queryFn: async () => {
            try {
                const res = await ApiClients.Notification.apiNotificationGet(
                    undefined,
                    false,
                    10,
                    0,
                    API_V_LATEST,
                );
                return res.data;
            } catch (err) {
                void handleErrorToast(err);
                return [];
            }
        },
        staleTime: STALE_TIME_30_SECONDS,
        refetchInterval: REFETCH_INTERVAL_1_MINUTE,
    });
};

export const usePendingNotifications = (
    limit: number = 50,
    offset: number = 0,
) => {
    return useRoleGatedQuery([Roles.ADMIN], {
        queryKey: [UserNotificationQueryKeys.pending, limit, offset],
        queryFn: async () => {
            try {
                const res =
                    await ApiClients.Notification.apiNotificationPendingGet(
                        limit,
                        offset,
                        API_V_LATEST,
                    );
                return res.data;
            } catch (err) {
                void handleErrorToast(err);
                return [];
            }
        },
        staleTime: STALE_TIME_30_SECONDS,
    });
};

export const useMarkNotificationsAsRead = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: NotificationMarkAsReadRequestDto) => {
            const res =
                await ApiClients.Notification.apiNotificationMarkReadPost(
                    API_V_LATEST,
                    data,
                );
            return res.data;
        },
        onSuccess: () => {
            invalidateQueryKeys(queryClient, [
                UserNotificationQueryKeys.notifications,
                UserNotificationQueryKeys.summary,
                UserNotificationQueryKeys.undismissed,
            ]);
        },
        onError: handleErrorToast,
    });
};

export const useMarkNotificationsAsDismissed = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: NotificationMarkAsDismissedRequestDto) => {
            const res =
                await ApiClients.Notification.apiNotificationMarkDismissedPost(
                    API_V_LATEST,
                    data,
                );
            return res.data;
        },
        onSuccess: () => {
            invalidateQueryKeys(queryClient, [
                UserNotificationQueryKeys.notifications,
                UserNotificationQueryKeys.summary,
                UserNotificationQueryKeys.undismissed,
            ]);
        },
        onError: handleErrorToast,
    });
};

export const useCreateNotifications = () => {
    const queryClient = useQueryClient();

    return useRoleGatedMutation([Roles.ADMIN], {
        mutationFn: async (data: UserNotificationCreateRequestDto) => {
            const res = await ApiClients.Notification.apiNotificationCreatePost(
                API_V_LATEST,
                data,
            );
            return res.data;
        },
        onSuccess: () => {
            invalidateQueryKeys(queryClient, [
                UserNotificationQueryKeys.pending,
            ]);
            toast.success(i18next.t("notifications.created"));
        },
        onError: handleErrorToast,
    });
};

export const useDeleteNotification = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            const res = await ApiClients.Notification.apiNotificationIdDelete(
                id,
                API_V_LATEST,
            );
            return res.data;
        },
        onSuccess: () => {
            invalidateQueryKeys(queryClient, [
                UserNotificationQueryKeys.notifications,
                UserNotificationQueryKeys.summary,
                UserNotificationQueryKeys.pending,
                UserNotificationQueryKeys.undismissed,
            ]);
            toast.success(i18next.t("notifications.deleted"));
        },
        onError: handleErrorToast,
    });
};
